// File: src/pages/RegisterStyles.js

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

/**
 * Primary CTA — compact black active, soft gray disabled.
 */
export function getPrimaryButtonStyle(isActive = true, maxWidth = 300) {
  return {
    width: '100%',
    maxWidth,
    height: 42,
    padding: '0 14px',
    fontSize: 12.5,
    border: 'none',
    borderRadius: 10,
    color: '#fff',
    cursor: isActive ? 'pointer' : 'not-allowed',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    marginTop: 0,
    background: isActive ? '#0b0b0b' : '#bdbdbd',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    WebkitTapHighlightColor: 'transparent',
  };
}

// Narrow step container used by short forms like Verify Code
export const stepContainerNarrow = {
  textAlign: 'center',
  padding: '1rem',
  maxWidth: 450,
  margin: '0 auto',
};

// Row that holds the 3 code inputs
export const codeInputsRowStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: 10,
  marginBottom: '1.25rem',
};

// Single-digit code input
export function getCodeDigitInputStyle({ error = false, filled = false } = {}) {
  return {
    width: 52,
    height: 52,
    fontSize: '1.35rem',
    textAlign: 'center',
    border: `1.5px solid ${error ? '#EF4444' : filled ? '#d8d8d8' : '#e5e7eb'}`,
    borderRadius: 10,
    boxSizing: 'border-box',
    background: '#fff',
    color: '#0b0b0b',
    outline: 'none',
    fontWeight: 650,
    letterSpacing: '-0.02em',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  };
}

// “Didn’t get the code?” text + link
export const resendTextStyle = {
  fontSize: '0.9rem',
  color: '#6b7280',
  marginTop: '1.75rem',
  marginBottom: '2rem',
  lineHeight: 1.45,
  letterSpacing: '-0.01em',
};

export const resendLinkStyle = {
  cursor: 'pointer',
  color: '#0b0b0b',
  fontWeight: 800,
  letterSpacing: '-0.01em',
};

/**
 * Secondary / outline button.
 */
export function getSecondaryButtonStyle(maxWidth = 300) {
  return {
    width: '100%',
    maxWidth,
    height: 40,
    padding: '0 14px',
    fontSize: 12.5,
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 10,
    color: '#0b0b0b',
    cursor: 'pointer',
    marginTop: '0.75rem',
    fontWeight: 760,
    letterSpacing: '-0.01em',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    WebkitTapHighlightColor: 'transparent',
  };
}

// -------------------------
// Base / shared styles
// -------------------------
export const pageStyle = {
  fontFamily:
    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  backgroundColor: '#fff',
  margin: 0,
  position: 'relative',
  color: '#0b0b0b',
};

export const containerStyle = {
  maxWidth: 460,
  width: '90%',
  margin: '1.25rem auto',
  textAlign: 'center',
  position: 'relative',
};

export const shapeContainerStyle = {
  width: 'calc(100% - 1rem)',
  margin: '0rem auto',
  backgroundColor: '#000',
  borderRadius: 8,
  boxSizing: 'border-box',
  height: 250,
};

// -------------------------
// Step headings + icon badge
// -------------------------
export const headingCircleContainer = {
  width: 50,
  height: 50,
  borderRadius: 12,
  background: '#f3f4f6',
  display: 'grid',
  placeItems: 'center',
  margin: '0px auto 10px',
};

export const stepIconStyle = {
  fontSize: '1.5rem',
  color: '#000',
};

export const stepHeadingStyle = {
  fontSize: '1.35rem',
  fontWeight: 850,
  color: '#0b0b0b',
  margin: '12px 0 8px',
  letterSpacing: '-0.035em',
  lineHeight: 1.05,
};

export const stepSubTitleStyle = {
  fontSize: '0.92rem',
  color: 'rgba(17,17,17,0.58)',
  margin: '0 0 1.35rem 0',
  lineHeight: 1.48,
  fontWeight: 500,
  letterSpacing: '-0.01em',
};

// Legacy title (kept for compatibility, but prefer stepHeadingStyle)
export const TitleStyle = {
  fontSize: '0rem',
  fontWeight: 900,
  color: '#0b0b0b',
  marginBottom: '0.5rem',
  textAlign: 'center',
  marginTop: '1rem',
  zIndex: 100000,
};

// Dividing line
export const lineStyle = {
  backgroundColor: '#e5e7eb',
  width: '100%',
  maxWidth: 300,
  height: 1,
  margin: '1rem auto',
};

// Labels + inputs
export const labelStyle = {
  fontSize: '0.76rem',
  color: 'rgba(17,17,17,0.62)',
  marginBottom: '7px',
  fontWeight: 720,
  display: 'block',
  textAlign: 'left',
  width: '100%',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

export const inputStyle = {
  width: '100%',
  padding: '0.72rem 0.75rem',
  fontSize: '0.95rem',
  border: '1px solid #e1e1e1',
  borderRadius: 10,
  boxSizing: 'border-box',
  backgroundColor: '#fff',
  color: '#0b0b0b',
  outline: 'none',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  fontFamily:
    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

// Highlight chip — used in code/email callouts
export const highlightStyle = {
  backgroundColor: '#f3f4f6',
  color: '#0b0b0b',
  fontWeight: 800,
  padding: '2px 8px',
  borderRadius: 6,
  display: 'inline-block',
  marginTop: 10,
  letterSpacing: '-0.01em',
};

export const subTitleStyle = {
  fontSize: '0.92rem',
  color: 'rgba(17,17,17,0.58)',
  margin: '0 0 1.25rem 0',
  lineHeight: 1.48,
  fontWeight: 500,
  letterSpacing: '-0.01em',
};

export const alreadyHaveAccountStyle = {
    marginTop: 24,
    fontSize: '0.9rem',
    color: 'rgba(17,17,17,0.62)',
    textAlign: 'center',
    fontWeight: 500,
    letterSpacing: '-0.012em',
  };

const forgotLinkStyle = {
  fontSize: 12,
  color: '#666',
  cursor: 'pointer',
  textDecoration: 'none',
  position: 'absolute',
  right: 0,
  bottom: '-25px',
  fontWeight: 600,
};

export const loginLinkStyle = {
  marginLeft: 5,
  cursor: 'pointer',
  color: '#0b0b0b',
  fontWeight: 850,
    fontSize: '0.9rem',
  textDecoration: 'none',
};

// -------------------------
// Header
// -------------------------
export const headerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

// Centered logo row
export const headerLogoRowStyle = {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '0.5rem',
};

export const logoSvgStyle = {
  width: 72,
  height: 'auto',
  display: 'block',
};

// 44px row height
export const headerNavRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  height: 44,
  padding: '0.5rem 1rem',
};

// Fixed 40x28 black pill
export const previousBackButtonStyle = {
  position: 'absolute',
  left: 16,
  background: '#000',
  color: '#fff',
  borderRadius: 9999,
  width: 40,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: 'none',
};

export const pageTitlePlainStyle = {
  fontSize: '0.95rem',
  fontWeight: 900,
  lineHeight: 1,
  textTransform: 'uppercase',
  letterSpacing: 0.2,
    color: '#0b0b0b',
  width: '100%',
  textAlign: 'center',
  zIndex: 1000,
};



export const progressContainerStyle = {
  position: 'absolute',
  right: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
};

// -------------------------
// Step 1 helpers
// -------------------------
export const brandTitleStyleLogin = {
  fontSize: '1.6rem',
  lineHeight: 1,
  fontWeight: 900,
  color: '#0b0b0b',
  margin: '4px 0 10px',
  letterSpacing: '-0.045em',
  textTransform: 'uppercase',
};

export const subTitleStyleLogin = {
  maxWidth: 320,
  fontSize: '0.9rem',
  color: 'rgba(17,17,17,0.62)',
  margin: '0 auto 1.5rem',
  lineHeight: 1.35,
  fontWeight: 500,
  letterSpacing: '-0.01em',
};

export const formContainerWide = {
  width: '100%',
  maxWidth: 320,
  margin: '0 auto',
};

export const formRow2Col = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.65rem',
};

export const labelUpperLogin = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 750,
  color: 'rgba(17,17,17,0.62)',
  marginBottom: 7,
  textAlign: 'left',
  width: '100%',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

export const inputWideLogin = {
  width: '100%',
  maxWidth: 320,
  height: 48,
  marginBottom: 8,
  padding: '0 14px',
  fontSize: '0.9rem',
  borderRadius: 14,
  boxSizing: 'border-box',
  background: '#f5f5f5',
  border: '1px solid transparent',
  outline: 'none',
  color: '#0b0b0b',
  fontWeight: 520,
  letterSpacing: '-0.01em',
  fontFamily:
    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

// -------------------------
// Step 1 top bar + toggle
// -------------------------
export const topBarStyleStep1 = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem',
  zIndex: 4,
  position: 'relative',
  background: 'none',
  marginBottom: '1rem',
};

export const blackBackButtonStyle = {
  backgroundColor: '#000',
  color: '#fff',
  border: 'none',
  borderRadius: 9999,
  padding: '0px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '1rem',
  height: 28,
};

export const toggleContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f3f4f6',
  borderRadius: 9999,
  overflow: 'hidden',
  padding: '4px',
  height: 28,
};

export function toggleItemStyle(selected) {
  return {
    padding: '0.4rem 0.7rem',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: selected ? '#000' : '#555',
    backgroundColor: selected ? '#fff' : 'transparent',
    transition: 'background-color 0.2s ease',
    userSelect: 'none',
  };
}

// -------------------------
// Legacy logo wordmark
// -------------------------
export const tribeLogoStyle = {
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#fff',
  textTransform: 'lowercase',
  backgroundColor: '#000',
  padding: '0.2rem .95rem',
  borderRadius: '4px',
};

// -------------------------------------------
// Action Sheet Styles
// -------------------------------------------
export const actionSheetStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 99999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  sheetContainer: {
    width: '100%',
    maxWidth: 420,
    margin: '0 8px',
    marginBottom: isPWA() ? 40 : 8,
    animation: 'slideUp 0.3s ease-out',
  },

  optionContainer: {
    backgroundColor: '#F9F9F9',
    borderRadius: 8,
    overflow: 'hidden',
  },

  optionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 16px',
    fontSize: '1rem',
    cursor: 'pointer',
    color: '#000',
  },

  optionRowTop: {
    borderBottom: '1px solid #eee',
  },

  optionRowBottom: {},

  iconRight: {
    fontSize: '1.2rem',
    color: '#888',
  },

  cancelRow: {
    marginTop: 12,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    textAlign: 'center',
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 600,
    color: '#333',
    cursor: 'pointer',
    marginBottom: isPWA() ? 40 : 8,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
};